import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Smartphone, Download, ExternalLink, ShieldCheck } from 'lucide-react';
import { route, DriverOnboardingPage } from './routeCommon';
import { useCoreContext } from '../../lib/coreContext';
import { UserRole } from '../../lib/contracts';
import { FEATURE_ROUTES } from '../routeConstants';

const NativeDriverAppRedirectCard: React.FC = () => {
  const { currentUser } = useCoreContext();
  const isOwner = currentUser?.role === UserRole.OWNER;

  if (isOwner) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-6">
        <div className="bg-ninpo-card border border-white/10 rounded-[2.5rem] p-8 md:p-12 max-w-xl w-full text-center space-y-6 shadow-2xl">
          <div className="w-16 h-16 rounded-3xl bg-ninpo-lime/10 border border-ninpo-lime/20 flex items-center justify-center mx-auto text-ninpo-lime">
            <Smartphone className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black uppercase text-white tracking-widest">
              Deliveries run in the Owner app
            </h2>
            <p className="text-xs font-bold text-ninpo-lime uppercase tracking-widest mt-2">
              Native Android · Deliver tab
            </p>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">
            As owner you can shop and deliver yourself — that lives in the <strong>Ninpo Owner</strong> Android
            app (Deliver tab), not the public Driver Play listing and not this browser page. Use Owner console on
            the web for catalog, approvals, and ops.
          </p>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-left space-y-2">
            <div className="flex items-center gap-2 text-[11px] font-bold text-white uppercase tracking-wider">
              <ShieldCheck className="w-4 h-4 text-ninpo-lime shrink-0" />
              <span>Owner delivery setup:</span>
            </div>
            <ul className="text-[10px] text-slate-400 space-y-1 list-disc list-inside">
              <li>Install the internal Owner APK (`com.ninpo.owner`)</li>
              <li>Complete owner fulfillment setup on first launch</li>
              <li>Go ONLINE as shopper (Shop + Deliver) when you want assignments</li>
              <li>Use the Deliver tab for the live queue, shopping, and handoff</li>
            </ul>
          </div>
          <div className="pt-2 flex flex-col sm:flex-row gap-3">
            <Link
              to={FEATURE_ROUTES.admin.productQueue}
              className="flex-1 py-4 bg-ninpo-lime text-ninpo-black font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-ninpo-lime/90 transition flex items-center justify-center gap-2 shadow-neon"
            >
              Open Owner console
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-6">
      <div className="bg-ninpo-card border border-white/10 rounded-[2.5rem] p-8 md:p-12 max-w-xl w-full text-center space-y-6 shadow-2xl">
        <div className="w-16 h-16 rounded-3xl bg-ninpo-lime/10 border border-ninpo-lime/20 flex items-center justify-center mx-auto text-ninpo-lime">
          <Smartphone className="w-8 h-8" />
        </div>
        <div>
          <h2 className="text-2xl font-black uppercase text-white tracking-widest">
            Ninpo Driver App Required
          </h2>
          <p className="text-xs font-bold text-ninpo-lime uppercase tracking-widest mt-2">
            Native Android App Only
          </p>
        </div>
        <p className="text-xs text-slate-300 leading-relaxed">
          Driver deliveries, live GPS dispatch, background location tracking, and in-app barcode bottle scanning are exclusively supported on the official <strong>Ninpo Driver Android App</strong>. Web browser driver access is disabled.
        </p>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-left space-y-2">
          <div className="flex items-center gap-2 text-[11px] font-bold text-white uppercase tracking-wider">
            <ShieldCheck className="w-4 h-4 text-ninpo-lime shrink-0" />
            <span>Driver Native Features:</span>
          </div>
          <ul className="text-[10px] text-slate-400 space-y-1 list-disc list-inside">
            <li>Foreground Fused Location Service & turn-by-turn map navigation</li>
            <li>In-app camera barcode scanner with haptic feedback</li>
            <li>Instant 50% bottle return fee payouts to driver wallet</li>
            <li>Real-time Socket.IO dispatch queue alerts</li>
          </ul>
        </div>
        <div className="pt-2 flex flex-col sm:flex-row gap-3">
          <a
            href="https://play.google.com/store/apps/details?id=com.ninpo.driver"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-4 bg-ninpo-lime text-ninpo-black font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-ninpo-lime/90 transition flex items-center justify-center gap-2 shadow-neon"
          >
            <Download className="w-4 h-4" />
            Download Android App
          </a>
          <a
            href="ninpodriver://open"
            className="px-6 py-4 bg-white/10 text-white font-black text-[11px] uppercase tracking-widest rounded-2xl hover:bg-white/20 transition flex items-center justify-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Open App
          </a>
        </div>
      </div>
    </div>
  );
};

export const driverManifestRoutes: RouteObject[] = [
  { path: 'onboarding', element: route(<DriverOnboardingPage />) },
  { path: 'driver/onboarding', element: route(<DriverOnboardingPage />) },
  { path: '*', element: route(<NativeDriverAppRedirectCard />) }
];
